import { useEffect, useMemo, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { useCardsStore } from '@/stores/cardsStore'
import { useUserStore } from '@/stores/userStore'
import { getErrorMessage } from '@/utils/error'
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  TablePagination,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Card,
  CardContent,
  useMediaQuery,
  Collapse,
  Button,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  Add as AddIcon,
  LibraryAdd as BulkAddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  MenuBook as ImportDictionaryIcon,
} from '@mui/icons-material'
import { CardItem } from '@/components/cards/CardItem'
import { CardListItem } from '@/components/cards/CardListItem'
import { CreateCardModal } from '@/components/cards/CreateCardModal'
import { BulkCreateCardsModal } from '@/components/cards/BulkCreateCardsModal'
import { CardBulkDeleteDialog } from '@/components/bulk/CardBulkDeleteDialog'
import { tagsApi, type Tag } from '@/api/tagsApi'
import { dictionariesApi } from '@/api/dictionaries'
import { cardsApi } from '@/api/cardsApi'
import type { Dictionary } from '@/types'
import { useEnumsStore } from '@/stores/enumsStore'
import type { Card as CardType } from '@/types'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import '@/styles/dashboard.css'

export function CardsPage() {
  const { id: userIdParam } = useParams<{ id?: string }>()
  const userId = userIdParam ? parseInt(userIdParam, 10) : undefined
  const isTeacherView = userId != null
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { t } = useTranslation()
  const {
    cards,
    isLoading,
    error,
    pagination,
    loadCards,
    deleteCard,
    clearError,
  } = useCardsStore()

  const { fetchUser } = useUserStore()

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [exact, setExact] = useState(false)
  const [orderBy, setOrderBy] = useState<'created_at' | 'updated_at' | 'progress'>('created_at')
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc')

  const [progressMin, setProgressMin] = useState<number | ''>('')
  const [progressMax, setProgressMax] = useState<number | ''>('')
  const [withoutTranslation, setWithoutTranslation] = useState(false)
  const [withoutTranscription, setWithoutTranscription] = useState(false)
  const [withoutAudio, setWithoutAudio] = useState(false)
  const [tagsFilter, setTagsFilter] = useState<number[]>([])
  const [tagsList, setTagsList] = useState<Tag[]>([])
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [partOfSpeech, setPartOfSpeech] = useState('')
  const [wordType, setWordType] = useState<'word' | 'phrase' | ''>('')

  /**
   * Массовые операции доступны учителю на странице ученика.
   * Выделение переживает пагинацию: Set хранит id со всех просмотренных страниц.
   */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)

  const { enums, loadEnums } = useEnumsStore()

  const sortPreset = useMemo(() => {
    if (orderBy === 'created_at' && orderDirection === 'desc') return 'created_desc'
    if (orderBy === 'created_at' && orderDirection === 'asc') return 'created_asc'
    if (orderBy === 'updated_at' && orderDirection === 'desc') return 'updated_desc'
    if (orderBy === 'updated_at' && orderDirection === 'asc') return 'updated_asc'
    if (orderBy === 'progress' && orderDirection === 'desc') return 'progress_desc'
    if (orderBy === 'progress' && orderDirection === 'asc') return 'progress_asc'
    return 'created_desc'
  }, [orderBy, orderDirection])

  useEffect(() => {
    if (isTeacherView && userId) {
      fetchUser(userId).catch(() => {})
    }
  }, [isTeacherView, userId, fetchUser])

  useEffect(() => {
    tagsApi.getList().then((r) => setTagsList(r.data)).catch(() => {})
  }, [])

  useEffect(() => {
    loadEnums().catch(() => {})
  }, [loadEnums])

  const buildLoadParams = (overrides?: { page?: number; per_page?: number }) => {
    const p: Parameters<typeof loadCards>[0] = {
      user_id: userId,
      search: search || undefined,
      progress_min: progressMin === '' ? undefined : progressMin,
      progress_max: progressMax === '' ? undefined : progressMax,
      tags: tagsFilter.length > 0 ? tagsFilter : undefined,
      part_of_speech: partOfSpeech || undefined,
      type: wordType || undefined,
      order_by: orderBy,
      order_direction: orderDirection,
      page: overrides?.page ?? 1,
      per_page: overrides?.per_page ?? pagination.per_page,
    }
    if (exact) p.exact = true
    if (withoutTranslation) p.without_translation = true
    if (withoutTranscription) p.without_transcription = true
    if (withoutAudio) p.without_audio = true
    return p
  }

  useEffect(() => {
    loadCards(buildLoadParams()).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    search,
    exact,
    progressMin,
    progressMax,
    tagsFilter,
    partOfSpeech,
    wordType,
    withoutTranslation,
    withoutTranscription,
    withoutAudio,
    orderBy,
    orderDirection,
    pagination.per_page,
    loadCards,
  ])

  const handlePageChange = (_: unknown, newPage: number) => {
    loadCards(buildLoadParams({ page: newPage + 1 })).catch(() => {})
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const perPage = parseInt(e.target.value, 10)
    loadCards(buildLoadParams({ page: 1, per_page: perPage })).catch(() => {})
  }

  const handleCreateSuccess = () => {
    setCreateModalOpen(false)
    loadCards(buildLoadParams({ page: pagination.current_page })).catch(() => {})
  }

  const handleBulkSuccess = () => {
    setBulkModalOpen(false)
    loadCards(buildLoadParams()).catch(() => {})
  }

  const handleDelete = async (card: CardType) => {
    if (!window.confirm(t('common.delete') + '?')) return
    try {
      await deleteCard(card.id)
    } catch {
      // error in store
    }
  }

  const toggleSelected = (cardId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(cardId)) {
        next.delete(cardId)
      } else {
        next.add(cardId)
      }
      return next
    })
  }

  const allOnPageSelected = cards.length > 0 && cards.every((c) => selectedIds.has(c.id))
  const someOnPageSelected = cards.some((c) => selectedIds.has(c.id))

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        cards.forEach((c) => next.delete(c.id))
      } else {
        cards.forEach((c) => next.add(c.id))
      }
      return next
    })
  }

  const handleBulkFinished = () => {
    setBulkDeleteOpen(false)
    setSelectedIds(new Set())
    loadCards(buildLoadParams({ page: pagination.current_page })).catch(() => {})
  }

  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importDictionaries, setImportDictionaries] = useState<Dictionary[]>([])
  const [importDictionaryId, setImportDictionaryId] = useState<number | ''>('')
  const [importLoading, setImportLoading] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importDictionariesLoading, setImportDictionariesLoading] = useState(false)

  const openImportDictionaryModal = () => {
    if (!isTeacherView || !userId) return
    setImportModalOpen(true)
    setImportError(null)
    setImportDictionaryId('')
    if (importDictionaries.length === 0) {
      setImportDictionariesLoading(true)
      dictionariesApi
        .getDictionaries({ per_page: 100 })
        .then((res) => setImportDictionaries(res.data))
        .catch((err: Error) => setImportError(err.message))
        .finally(() => setImportDictionariesLoading(false))
    }
  }

  const handleImportFromDictionary = async () => {
    if (!userId || !importDictionaryId) return
    setImportLoading(true)
    setImportError(null)
    try {
      let page = 1
      const perPage = 100
      const texts: string[] = []

      // Загружаем слова словаря постранично
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await dictionariesApi.getDictionaryWords(importDictionaryId, {
          page,
          per_page: perPage,
        })
        if (res.data.length === 0) break
        res.data.forEach((w) => {
          if (w.word) {
            texts.push(w.word)
          }
        })
        if (page >= res.last_page) break
        page += 1
      }

      const uniqueTexts = Array.from(new Set(texts.filter(Boolean)))
      if (uniqueTexts.length === 0) {
        setImportError(t('cards.import_from_dictionary_empty'))
        setImportLoading(false)
        return
      }

      const payload = {
        words: uniqueTexts.map((text) => ({ text })),
        user_id: userId,
      }
      await cardsApi.bulkCreateCards(payload)
      setImportModalOpen(false)
      loadCards(buildLoadParams()).catch(() => {})
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setImportError(errorMessage || t('cards.import_from_dictionary_error'))
    } finally {
      setImportLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <RouterLink
            to="/dashboard"
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </RouterLink>
          {isTeacherView && (
            <Typography color="text.primary">
              {t('cards.student_cards')}
            </Typography>
          )}
          {!isTeacherView && (
            <Typography color="text.primary">
              {t('cards.my_cards')}
            </Typography>
          )}
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {isTeacherView ? t('cards.student_cards') : t('cards.my_cards')}
          </Typography>
          <PageHelpPopover pageKey="cards" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
          />
          <Tooltip title={t('common.searchExactTooltip')}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={exact}
                  onChange={(e) => setExact(e.target.checked)}
                />
              }
              label={t('common.searchExact')}
            />
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: isMobile ? 140 : 220 }}>
            <InputLabel>{t('cards.sort_by')}</InputLabel>
            <Select
              value={sortPreset}
              label={t('cards.sort_by')}
              onChange={(e) => {
                const v = e.target.value as string
                if (v === 'created_desc') {
                  setOrderBy('created_at')
                  setOrderDirection('desc')
                } else if (v === 'created_asc') {
                  setOrderBy('created_at')
                  setOrderDirection('asc')
                } else if (v === 'updated_desc') {
                  setOrderBy('updated_at')
                  setOrderDirection('desc')
                } else if (v === 'updated_asc') {
                  setOrderBy('updated_at')
                  setOrderDirection('asc')
                } else if (v === 'progress_desc') {
                  setOrderBy('progress')
                  setOrderDirection('desc')
                } else if (v === 'progress_asc') {
                  setOrderBy('progress')
                  setOrderDirection('asc')
                }
              }}
            >
              <MenuItem value="created_desc">{t('cards.sort_presets.created_desc')}</MenuItem>
              <MenuItem value="created_asc">{t('cards.sort_presets.created_asc')}</MenuItem>
              <MenuItem value="updated_desc">{t('cards.sort_presets.updated_desc')}</MenuItem>
              <MenuItem value="updated_asc">{t('cards.sort_presets.updated_asc')}</MenuItem>
              <MenuItem value="progress_desc">{t('cards.sort_presets.progress_desc')}</MenuItem>
              <MenuItem value="progress_asc">{t('cards.sort_presets.progress_asc')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant={filtersOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((o) => !o)}
            sx={{ height: 40 }}
          >
            {t('cards.filters_button')}
          </Button>
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Tooltip title={t('cards.create_card')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setCreateModalOpen(true)}
                  aria-label={t('cards.create_card')}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('cards.create_bulk')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setBulkModalOpen(true)}
                  aria-label={t('cards.create_bulk')}
                >
                  <BulkAddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {isTeacherView && (
              <Tooltip title={t('cards.import_from_dictionary')}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={openImportDictionaryModal}
                    aria-label={t('cards.import_from_dictionary')}
                  >
                    <ImportDictionaryIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('cards.progress')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder={t('cards.filters.progress_from_placeholder')}
                    value={progressMin === '' ? '' : progressMin}
                    onChange={(e) => {
                      const n = e.target.value === '' ? '' : Number(e.target.value)
                      if (n === '' || (Number.isFinite(n) && n >= 0 && n <= 100)) setProgressMin(n)
                    }}
                    inputProps={{ inputMode: 'numeric', min: 0, max: 100, style: { width: 44, textAlign: 'center' } }}
                    sx={{ width: 56 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    –
                  </Typography>
                  <TextField
                    size="small"
                    placeholder={t('cards.filters.progress_to_placeholder')}
                    value={progressMax === '' ? '' : progressMax}
                    onChange={(e) => {
                      const n = e.target.value === '' ? '' : Number(e.target.value)
                      if (n === '' || (Number.isFinite(n) && n >= 0 && n <= 100)) setProgressMax(n)
                    }}
                    inputProps={{ inputMode: 'numeric', min: 0, max: 100, style: { width: 44, textAlign: 'center' } }}
                    sx={{ width: 56 }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('cards.filter.tags')}
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={tagsList}
                  value={tagsList.filter((tag) => tagsFilter.includes(tag.id))}
                  onChange={(_, newValue) => setTagsFilter(newValue.map((tag) => tag.id))}
                  getOptionLabel={(tag) => tag.name}
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} placeholder={t('common.search')} />
                  )}
                  sx={{ minWidth: 220 }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('cards.filter.part_of_speech')}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={partOfSpeech}
                    displayEmpty
                    onChange={(e) => setPartOfSpeech(e.target.value)}
                    renderValue={(v) => (v === '' ? t('cards.filter.all') : enums?.PartOfSpeech?.find((o) => o.value === v)?.label ?? v)}
                  >
                    <MenuItem value="">{t('cards.filter.all')}</MenuItem>
                    {(enums?.PartOfSpeech ?? []).map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('cards.filter.type')}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={wordType}
                    displayEmpty
                    onChange={(e) => setWordType((e.target.value || '') as 'word' | 'phrase' | '')}
                    renderValue={(v: string) => {
                      if (v === '' || !v) return t('cards.filter.all')
                      if (v === 'word') return t('cards.filter_type_word')
                      if (v === 'phrase') return t('cards.filter_type_phrase')
                      return v
                    }}
                  >
                    <MenuItem value="">{t('cards.filter.all')}</MenuItem>
                    <MenuItem value="word">{t('cards.filter_type_word')}</MenuItem>
                    <MenuItem value="phrase">{t('cards.filter_type_phrase')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={withoutTranslation}
                    onChange={(e) => setWithoutTranslation(e.target.checked)}
                  />
                }
                label={t('cards.filters.without_translation')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={withoutTranscription}
                    onChange={(e) => setWithoutTranscription(e.target.checked)}
                  />
                }
                label={t('cards.filters.without_transcription')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={withoutAudio}
                    onChange={(e) => setWithoutAudio(e.target.checked)}
                  />
                }
                label={t('cards.filters.without_audio')}
              />
            </Box>
          </Box>
        </Collapse>

        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Card key={i}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={28} />
                    <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
                    <Skeleton variant="rectangular" height={8} sx={{ mt: 1.5, borderRadius: 1 }} />
                  </CardContent>
                </Card>
              ))
            ) : cards.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                {t('cards.empty')}
              </Typography>
            ) : (
              cards.map((card) => (
                <CardListItem
                  key={card.id}
                  card={card}
                  onDelete={handleDelete}
                  showActions
                  userId={userId}
                  orderBy={orderBy}
                />
              ))
            )}
          </Box>
        ) : (
          <>
          {isTeacherView && selectedIds.size > 0 && (
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                mb: 1,
              }}
            >
              <Typography variant="body2">
                {t('cards.bulk.selectedCount', { count: selectedIds.size })}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => setSelectedIds(new Set())}>
                {t('cards.bulk.clearSelection')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => setBulkDeleteOpen(true)}
              >
                {t('cards.bulk.delete')}
              </Button>
            </Paper>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {isTeacherView && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onChange={toggleSelectAllOnPage}
                        inputProps={{ 'aria-label': t('cards.bulk.selectAll') }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{t('cards.word')}</TableCell>
                  <TableCell>{t('cards.transcription')}</TableCell>
                  <TableCell>{t('cards.translation')}</TableCell>
                  <TableCell>{t('cards.progress')}</TableCell>
                  <TableCell>{t('cards.clues')}</TableCell>
                  <TableCell>{t('cards.date')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={isTeacherView ? 8 : 7}><Skeleton height={40} /></TableCell>
                    </TableRow>
                  ))
                ) : cards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isTeacherView ? 8 : 7} align="center">
                      <Typography color="text.secondary">
                        {t('cards.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cards.map((card) => (
                    <CardItem
                      key={card.id}
                      card={card}
                      onDelete={handleDelete}
                      showActions
                      userId={userId}
                      orderBy={orderBy}
                      selectable={isTeacherView}
                      selected={selectedIds.has(card.id)}
                      onToggleSelected={toggleSelected}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            ...(isMobile && { flexDirection: 'column', alignItems: 'stretch' }),
          }}
        >
          <TablePagination
            component="div"
            count={pagination.total}
            page={Math.max(0, pagination.current_page - 1)}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.per_page}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={isMobile ? '' : t('cards.rows_per_page')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('cards.of')} ${count !== -1 ? count : `${t('cards.more_than')} ${to}`}`
            }
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
              ...(isMobile && { borderTop: 1, borderColor: 'divider', pt: 1 }),
            }}
          />
        </Box>

        <CreateCardModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          userId={userId}
        />
        <BulkCreateCardsModal
          open={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          onSuccess={handleBulkSuccess}
          userId={userId}
        />

        {/* Единственная массовая операция над карточками — удаление */}
        <CardBulkDeleteDialog
          open={bulkDeleteOpen}
          cardIds={Array.from(selectedIds)}
          onClose={() => setBulkDeleteOpen(false)}
          onFinished={handleBulkFinished}
        />

        <Dialog
          open={importModalOpen}
          onClose={() => {
            if (importLoading) return
            setImportModalOpen(false)
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('cards.import_from_dictionary_title')}</DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 1.5 }}>
            {importError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {importError}
              </Alert>
            )}
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
              <InputLabel>{t('cards.import_from_dictionary_select')}</InputLabel>
              <Select
                label={t('cards.import_from_dictionary_select')}
                value={importDictionaryId === '' ? '' : importDictionaryId}
                onChange={(e) => {
                  const v = e.target.value as number | ''
                  setImportDictionaryId(v)
                }}
                disabled={
                  importLoading ||
                  importDictionariesLoading ||
                  importDictionaries.length === 0
                }
              >
                {importDictionaries.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1.5 }}>
            <Button onClick={() => setImportModalOpen(false)} disabled={importLoading}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleImportFromDictionary}
              disabled={importLoading || !importDictionaryId}
            >
              {t('cards.import_from_dictionary_action')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
